import { useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';
import { requestUntiSyncRun, useSystemStatus } from '../../api/system-status';
import { Badge, ErrorState } from '../../components/ui';
import { formatDateTimeMsk } from '../dashboard/format';
import {
  overallLabel,
  overallTone,
  parseSystemStatus,
  pendingAppliesTone,
  statusRows,
  TONE_LABEL,
  uptimeLabel,
  versionLabel,
} from './settings-model';

/**
 * ADDITIVE (v1.9.38, T.U1) — the «Обновить сейчас» button of the «Синк УНТИ
 * (API)» row (contract #35). The click ARMS an out-of-band sweep (202 — the
 * worker's drain poll sweeps within ~30 s, the row's own light moves on its
 * next 30 s poll) and a FRESH pending request answers 409
 * UNTI_SYNC_RUN_PENDING, rendered as the row's own «уже идёт» line — a refusal
 * of the double-click, never a red error. ADMIN-only: showing it below that
 * role would be a button that can only answer 403 (the SnapshotsTab rule).
 */
export function UntiSyncRunButton() {
  const { role } = useAuth();
  const canRun = hasAtLeast(role, 'ADMIN');
  const run = useMutation({ mutationFn: () => requestUntiSyncRun() });
  // The refusal and the confirmation share one line: the LAST outcome speaks.
  const [note, setNote] = useState<string | null>(null);

  if (!canRun) return null;

  const onRun = () => {
    setNote(null);
    run.mutate(undefined, {
      onSuccess: () => setNote('Внеочередной синк запущен — данные обновятся в течение минуты.'),
      onError: (error) => {
        const code = typeof (error as { code?: unknown }).code === 'string' ? String((error as { code?: unknown }).code) : '';
        setNote(
          code === 'UNTI_SYNC_RUN_PENDING'
            ? 'Синк уже идёт — дождитесь завершения.'
            : 'Не удалось запустить синк. Попробуйте ещё раз.',
        );
      },
    });
  };

  return (
    <p className="status-badge-line" data-testid="unti-sync-run-line">
      <button
        type="button"
        className="btn btn--secondary"
        data-testid="unti-sync-run-button"
        onClick={onRun}
        disabled={run.isPending}
      >
        {run.isPending ? 'Запускаем…' : 'Обновить сейчас'}
      </button>
      {note ? (
        <span data-testid="unti-sync-run-note" data-code={run.error instanceof Error ? '409' : undefined}>
          {note}
        </span>
      ) : null}
    </p>
  );
}

/**
 * И8 — the «как у нас дела» panel (contract #23, task T.C5).
 *
 * It polls #23 every 30s (the hook owns the cadence) and renders one traffic light
 * per dependency; a light that has nothing to report is a grey «нет данных», not a
 * green. T.X2 (owner 2026-09-11): the `db`/`api`/`redis` probes are NOT drawn —
 * which database and cache sit behind the api is internal infrastructure. #23
 * still answers `db`/`redis`; the panel renders the worker, the two beats and the
 * two snapshot sources only.
 */
export function StatusPanel() {
  const status = useSystemStatus();

  // ONE `now` per answer: every row is compared against the same instant, so a
  // slow render can never make two rows disagree about how old a timestamp is.
  // A refetch moves `dataUpdatedAt`, which is exactly when the clock must move.
  const now = useMemo(() => Date.now(), [status.dataUpdatedAt]);

  if (status.isPending) {
    return (
      <div className="card" data-testid="status-skeleton">
        <div className="status-grid">
          {Array.from({ length: 4 }, (_, index) => (
            <div className="status-cell" key={index}>
              <div className="skeleton skeleton--line skeleton--wide" />
              <div className="skeleton skeleton--line" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (status.isError) {
    return (
      <div className="card">
        <ErrorState
          message={status.error instanceof Error ? status.error.message : 'Не удалось получить статус.'}
          onRetry={() => void status.refetch()}
        />
      </div>
    );
  }

  const payload = parseSystemStatus(status.data);
  const rows = statusRows(payload, now);
  const summary = overallTone(rows.map((row) => row.tone));
  const badgeTone = pendingAppliesTone(payload.pendingApplies, payload.stalledApplies);
  // T.Z40 — the stalled count is the escalation of the same queue: a stalled
  // apply is the worker not draining, so the badge says THAT and reads danger.
  const badgeStalled = (payload.stalledApplies ?? 0) > 0;

  return (
    <>
      <div className="card" data-testid="status-panel">
        <div className="status-head">
          <p className="status-summary" data-testid="status-summary" data-tone={summary}>
            <span className={`status-light status-light--${summary}`} aria-hidden="true" />
            {overallLabel(summary)}
          </p>
          <p className="status-refresh" data-testid="status-refresh">
            обновляется каждые 30 с
            {status.dataUpdatedAt ? ` · обновлено ${formatDateTimeMsk(new Date(status.dataUpdatedAt).toISOString())}` : ''}
          </p>
        </div>

        {badgeTone === 'ok' ? null : (
          <p className="status-badge-line" data-testid="badge-pending-applies-line">
            <Badge
              tone={badgeTone}
              title={
                badgeStalled
                  ? 'Есть свежие снапшоты, применение которых не завершилось за отведённое время — проверьте воркер'
                  : 'Снапшоты загружены вручную и ждут применения воркером'
              }
            >
              <span
                data-testid="badge-pending-applies"
                data-count={badgeStalled ? payload.stalledApplies : payload.pendingApplies}
                data-stalled={badgeStalled ? 'true' : undefined}
              >
                {badgeStalled
                  ? `применение задерживается: ${payload.stalledApplies}`
                  : `ждут применения: ${payload.pendingApplies}`}
              </span>
            </Badge>
          </p>
        )}

        <div className="status-grid">
          {rows.map((row) => (
            <div
              className="status-cell"
              key={row.key}
              data-testid="status-row"
              data-key={row.key}
              data-tone={row.tone}
            >
              <p className="status-cell__head">
                <span className={`status-light status-light--${row.tone}`} aria-hidden="true" />
                <span className="status-cell__label">{row.label}</span>
                <span className="status-cell__tone" data-testid={`status-tone-${row.key}`}>
                  {row.toneLabel ?? TONE_LABEL[row.tone]}
                </span>
              </p>
              <p className="status-cell__detail" data-testid={`status-detail-${row.key}`}>
                {row.detail}
              </p>
              {row.errorCode ? (
                <p className="status-cell__error" data-testid={`status-error-${row.key}`}>
                  код: {row.errorCode}
                </p>
              ) : null}
            </div>
          ))}
        </div>

        <UntiSyncRunButton />
      </div>

      <StatusFooter version={payload.version} uptime={payload.uptime} />
    </>
  );
}

function StatusFooter({ version, uptime }: { version: string | undefined; uptime: number | undefined }) {
  return (
    <p className="status-footer" data-testid="status-footer">
      версия <span data-testid="status-version">{versionLabel(version)}</span> · аптайм{' '}
      <span data-testid="status-uptime">{uptimeLabel(uptime)}</span>
    </p>
  );
}
